import {
  CartErrorEvent,
  CartLinesUpdateEvent,
} from '@shopify/events';

const FOXENA_BOX_SELECTOR = '[data-foxena-build-box]';
const initializedSections = new WeakSet();

class FoxenaBuildBox {
  constructor(section) {
    this.section = section;

    this.cards = Array.from(
      section.querySelectorAll('[data-box-product]')
    );

    this.countOutput = section.querySelector(
      '[data-box-count]'
    );

    this.messageOutput = section.querySelector(
      '[data-box-tier-message]'
    );

    this.progressFill = section.querySelector(
      '[data-box-progress-fill]'
    );

    this.tiers = Array.from(
      section.querySelectorAll('[data-box-tier]')
    );

    this.clearButton = section.querySelector(
      '[data-box-clear]'
    );

    this.visualJars = section.querySelector(
      '[data-box-visual-jars]'
    );

    this.summaryTitle = section.querySelector(
      '[data-box-summary-title]'
    );

    this.selectionOutput = section.querySelector(
      '[data-box-selection]'
    );

    this.summaryCount = section.querySelector(
      '[data-box-summary-count]'
    );

    this.subtotalOutput = section.querySelector(
      '[data-box-subtotal]'
    );

    this.discountLabel = section.querySelector(
      '[data-box-discount-label]'
    );

    this.savingsOutput = section.querySelector(
      '[data-box-savings]'
    );

    this.totalOutput = section.querySelector(
      '[data-box-total]'
    );

    this.statusOutput = section.querySelector(
      '[data-box-status]'
    );

    this.addButtons = Array.from(
      section.querySelectorAll(
        '[data-box-add], [data-box-mobile-add]'
      )
    );

    this.mobileCount = section.querySelector(
      '[data-box-mobile-count]'
    );

    this.mobileSaving = section.querySelector(
      '[data-box-mobile-saving]'
    );

    this.maxJars =
      Number(section.dataset.maxJars) || 6;

    this.currency =
      section.dataset.currency || 'INR';

    this.locale =
      document.documentElement.lang || 'en-IN';

    this.discounts = {
      2: Number(section.dataset.discountTwo) || 5,
      3: Number(section.dataset.discountThree) || 10,
      4: Number(section.dataset.discountFour) || 12,
      5: Number(section.dataset.discountFive) || 15,

      uniqueSix:
        Number(section.dataset.discountSixUnique) || 20,
    };

    this.isAdding = false;
    this.pendingCartDrawerComponent = null;

    if (
      this.cards.length < 2 ||
      this.addButtons.length === 0
    ) {
      return;
    }

    this.bindEvents();
    this.updateInterface();
  }

  bindEvents() {
    this.cards.forEach((card) => {
      card
        .querySelector('[data-box-toggle]')
        ?.addEventListener('click', () => {
          if (this.isAdding) return;

          const quantity =
            this.getCardQuantity(card);

          this.setCardQuantity(
            card,
            quantity > 0 ? 0 : 1
          );
        });

      card
        .querySelector('[data-box-action="plus"]')
        ?.addEventListener('click', () => {
          if (
            this.isAdding ||
            this.getTotalQuantity() >= this.maxJars
          ) {
            return;
          }

          this.setCardQuantity(
            card,
            this.getCardQuantity(card) + 1
          );
        });

      card
        .querySelector('[data-box-action="minus"]')
        ?.addEventListener('click', () => {
          if (this.isAdding) return;

          this.setCardQuantity(
            card,
            Math.max(
              0,
              this.getCardQuantity(card) - 1
            )
          );
        });
    });

    this.clearButton?.addEventListener(
      'click',
      () => {
        if (this.isAdding) return;

        this.cards.forEach((card) => {
          card.dataset.quantity = '0';
        });

        this.setStatus('');
        this.updateInterface();
      }
    );

    this.addButtons.forEach((button) => {
      button.addEventListener(
        'click',
        () => this.addBoxToCart()
      );
    });
  }

  getCardQuantity(card) {
    return Math.max(
      0,
      Number(card.dataset.quantity) || 0
    );
  }

  setCardQuantity(
    card,
    requestedQuantity
  ) {
    const currentQuantity =
      this.getCardQuantity(card);

    const totalWithoutCard =
      this.getTotalQuantity() - currentQuantity;

    const allowedQuantity = Math.max(
      0,
      Math.min(
        requestedQuantity,
        this.maxJars - totalWithoutCard
      )
    );

    card.dataset.quantity =
      String(allowedQuantity);

    this.setStatus('');
    this.updateInterface();
  }

  getSelection() {
    return this.cards
      .map((card) => ({
        card,

        variantId:
          Number(card.dataset.variantId),

        productId:
          Number(card.dataset.productId),

        productName:
          card.dataset.productName ||
          'FOXENA Makhana',

        flavourName:
          card.dataset.flavourName ||
          card.dataset.productName ||
          'FOXENA',

        price:
          Number(card.dataset.price) || 0,

        imageSrc:
          card.dataset.imageSrc || '',

        quantity:
          this.getCardQuantity(card),
      }))
      .filter(
        (item) =>
          item.variantId &&
          item.quantity > 0
      );
  }

  getTotalQuantity(
    selection = this.getSelection()
  ) {
    return selection.reduce(
      (sum, item) =>
        sum + item.quantity,
      0
    );
  }

  getUniqueQuantity(
    selection = this.getSelection()
  ) {
    return selection.length;
  }

  getDiscount(
    totalQuantity,
    uniqueQuantity
  ) {
    if (
      totalQuantity === 6 &&
      uniqueQuantity === 6
    ) {
      return this.discounts.uniqueSix;
    }

    if (totalQuantity >= 5) {
      return this.discounts[5];
    }

    if (totalQuantity === 4) {
      return this.discounts[4];
    }

    if (totalQuantity === 3) {
      return this.discounts[3];
    }

    if (totalQuantity === 2) {
      return this.discounts[2];
    }

    return 0;
  }

  getProgressMessage(
    totalQuantity,
    uniqueQuantity,
    currentDiscount
  ) {
    if (totalQuantity === 0) {
      return (
        `Choose 2 jars to unlock ` +
        `${this.discounts[2]}% OFF`
      );
    }

    if (totalQuantity === 1) {
      return (
        `Add 1 more jar and unlock ` +
        `${this.discounts[2]}% OFF`
      );
    }

    if (totalQuantity === 2) {
      return (
        `${currentDiscount}% OFF unlocked ✓ ` +
        `Add 1 more jar for ` +
        `${this.discounts[3]}% OFF`
      );
    }

    if (totalQuantity === 3) {
      return (
        `${currentDiscount}% OFF unlocked ✓ ` +
        `Add 1 more jar for ` +
        `${this.discounts[4]}% OFF`
      );
    }

    if (totalQuantity === 4) {
      return (
        `${currentDiscount}% OFF unlocked ✓ ` +
        `Add 1 more jar for ` +
        `${this.discounts[5]}% OFF`
      );
    }

    if (
      totalQuantity === 5 &&
      uniqueQuantity === 5
    ) {
      return (
        `${currentDiscount}% OFF unlocked ✓ ` +
        `Add the last unique flavour for ` +
        `${this.discounts.uniqueSix}% OFF`
      );
    }

    if (totalQuantity === 5) {
      return (
        `${currentDiscount}% OFF unlocked ✓ ` +
        `Choose all 6 unique flavours for ` +
        `${this.discounts.uniqueSix}% OFF`
      );
    }

    if (
      totalQuantity === 6 &&
      uniqueQuantity === 6
    ) {
      return (
        `Maximum ` +
        `${this.discounts.uniqueSix}% OFF ` +
        `unlocked 🎉`
      );
    }

    return (
      `${this.discounts[5]}% OFF unlocked ✓ ` +
      `Replace repeats with all 6 unique ` +
      `flavours for ` +
      `${this.discounts.uniqueSix}% OFF`
    );
  }

  formatMoney(cents) {
    const amount =
      Number(cents || 0) / 100;

    try {
      return new Intl.NumberFormat(
        this.locale,
        {
          style: 'currency',
          currency: this.currency,

          maximumFractionDigits:
            amount % 1 === 0 ? 0 : 2,
        }
      ).format(amount);
    } catch (_) {
      return (
        `₹` +
        Math.round(amount)
          .toLocaleString('en-IN')
      );
    }
  }

  updateInterface() {
    const selection =
      this.getSelection();

    const totalQuantity =
      this.getTotalQuantity(selection);

    const uniqueQuantity =
      this.getUniqueQuantity(selection);

    const subtotal = selection.reduce(
      (sum, item) =>
        sum +
        item.price * item.quantity,
      0
    );

    const discountPercent =
      this.getDiscount(
        totalQuantity,
        uniqueQuantity
      );

    const savings = Math.round(
      subtotal *
      discountPercent /
      100
    );

    const estimatedTotal =
      subtotal - savings;

    const atCapacity =
      totalQuantity >= this.maxJars;

    this.cards.forEach((card) => {
      const quantity =
        this.getCardQuantity(card);

      const selected =
        quantity > 0;

      const toggle =
        card.querySelector(
          '[data-box-toggle]'
        );

      const minus =
        card.querySelector(
          '[data-box-action="minus"]'
        );

      const plus =
        card.querySelector(
          '[data-box-action="plus"]'
        );

      const output =
        card.querySelector('output');

      const badge =
        card.querySelector(
          '[data-box-quantity-badge]'
        );

      card.classList.toggle(
        'is-selected',
        selected
      );

      toggle?.setAttribute(
        'aria-pressed',
        selected ? 'true' : 'false'
      );

      if (toggle) {
        toggle.setAttribute(
          'aria-label',
          selected
            ? `Remove ${
                card.dataset.flavourName ||
                'flavour'
              } from the box`
            : `Select ${
                card.dataset.flavourName ||
                'flavour'
              }`
        );
      }

      if (output) {
        output.textContent =
          String(quantity);
      }

      if (badge) {
        badge.textContent =
          `×${quantity}`;

        badge.hidden = !selected;
      }

      if (minus) {
        minus.disabled =
          quantity === 0 ||
          this.isAdding;
      }

      if (plus) {
        plus.disabled =
          atCapacity ||
          this.isAdding;
      }
    });

    if (this.countOutput) {
      this.countOutput.textContent =
        String(totalQuantity);
    }

    if (this.progressFill) {
      this.progressFill.style.width =
        `${Math.min(
          100,
          totalQuantity /
          this.maxJars *
          100
        )}%`;
    }

    if (this.messageOutput) {
      this.messageOutput.textContent =
        this.getProgressMessage(
          totalQuantity,
          uniqueQuantity,
          discountPercent
        );
    }

    this.tiers.forEach((tier) => {
      const threshold =
        Number(tier.dataset.boxTier);

      const unlocked =
        threshold === 6
          ? (
              totalQuantity === 6 &&
              uniqueQuantity === 6
            )
          : totalQuantity >= threshold;

      tier.classList.toggle(
        'is-unlocked',
        unlocked
      );
    });

    if (this.clearButton) {
      this.clearButton.disabled =
        totalQuantity === 0 ||
        this.isAdding;
    }

    if (this.summaryTitle) {
      this.summaryTitle.textContent =
        totalQuantity
          ? `${totalQuantity} ${
              totalQuantity === 1
                ? 'jar'
                : 'jars'
            } selected`
          : 'Start building';
    }

    if (this.summaryCount) {
      this.summaryCount.textContent =
        `${totalQuantity} ${
          totalQuantity === 1
            ? 'jar'
            : 'jars'
        }`;
    }

    this.renderSelectionChips(
      selection
    );

    this.renderVisualJars(
      selection
    );

    if (this.subtotalOutput) {
      this.subtotalOutput.textContent =
        this.formatMoney(subtotal);
    }

    if (this.savingsOutput) {
      this.savingsOutput.textContent =
        `− ${this.formatMoney(savings)}`;
    }

    if (this.totalOutput) {
      this.totalOutput.textContent =
        this.formatMoney(
          estimatedTotal
        );
    }

    if (this.discountLabel) {
      this.discountLabel.textContent =
        discountPercent
          ? (
              `Estimated savings ` +
              `(${discountPercent}%)`
            )
          : 'Estimated savings';
    }

    if (this.mobileCount) {
      this.mobileCount.textContent =
        String(totalQuantity);
    }

    if (this.mobileSaving) {
      this.mobileSaving.textContent =
        discountPercent
          ? (
              `Save ${discountPercent}% · ` +
              `Pay ${
                this.formatMoney(
                  estimatedTotal
                )
              }`
            )
          : 'No savings yet';
    }

    this.updateAddButtons(
      totalQuantity,
      estimatedTotal,
      discountPercent
    );
  }

  renderSelectionChips(selection) {
    if (!this.selectionOutput) return;

    if (selection.length === 0) {
      this.selectionOutput.textContent =
        'Select any 2–6 jars.';

      return;
    }

    const fragment =
      document.createDocumentFragment();

    selection.forEach((item) => {
      const chip =
        document.createElement('span');

      chip.className =
        'fbbx__selection-chip';

      chip.textContent =
        `${item.flavourName} × ` +
        `${item.quantity}`;

      fragment.appendChild(chip);
    });

    this.selectionOutput
      .replaceChildren(fragment);
  }

  renderVisualJars(selection) {
    if (!this.visualJars) return;

    const flattened = [];

    selection.forEach((item) => {
      for (
        let index = 0;
        index < item.quantity;
        index += 1
      ) {
        flattened.push(item);
      }
    });

    const fragment =
      document.createDocumentFragment();

    flattened
      .slice(0, this.maxJars)
      .forEach((item) => {
        if (!item.imageSrc) return;

        const image =
          document.createElement('img');

        image.className =
          'fbbx__box-jar';

        image.src =
          item.imageSrc;

        image.alt = '';
        image.loading = 'lazy';
        image.decoding = 'async';

        fragment.appendChild(image);
      });

    this.visualJars
      .replaceChildren(fragment);
  }

  updateAddButtons(
    totalQuantity,
    estimatedTotal,
    discountPercent
  ) {
    const disabled =
      totalQuantity < 2 ||
      this.isAdding;

    this.addButtons.forEach(
      (button) => {
        button.disabled = disabled;

        if (this.isAdding) {
          button.setAttribute(
            'aria-busy',
            'true'
          );

          button.textContent =
            'Adding your box…';

          return;
        }

        button.removeAttribute(
          'aria-busy'
        );

        const isMobileButton =
          button.matches(
            '[data-box-mobile-add]'
          );

        if (totalQuantity < 2) {
          const remaining =
            2 - totalQuantity;

          button.textContent =
            isMobileButton
              ? `Select ${remaining} more`
              : (
                  `Select ${remaining} more ` +
                  `${
                    remaining === 1
                      ? 'jar'
                      : 'jars'
                  } to continue`
                );

          return;
        }

        button.textContent =
          isMobileButton
            ? (
                `Continue · ` +
                `${this.formatMoney(
                  estimatedTotal
                )}`
              )
            : (
                `Continue with ` +
                `${totalQuantity} jars · ` +
                `${discountPercent}% OFF`
              );
      }
    );
  }

  setStatus(
    message,
    isError = false
  ) {
    if (!this.statusOutput) return;

    this.statusOutput.textContent =
      message;

    this.statusOutput.classList.toggle(
      'is-error',
      isError
    );
  }

  getCartSectionIds() {
    return Array.from(
      document.querySelectorAll(
        'cart-items-component[data-section-id]'
      )
    )
      .map(
        (component) =>
          component.dataset.sectionId
      )
      .filter(Boolean)
      .filter(
        (
          value,
          index,
          array
        ) =>
          array.indexOf(value) === index
      )
      .slice(0, 5);
  }

  pauseCartDrawerAutoOpen() {
    const component =
      document.querySelector(
        '#cart-drawer ' +
        'cart-drawer-component[auto-open]'
      );

    if (!component) return;

    this.pendingCartDrawerComponent =
      component;

    component.removeAttribute(
      'auto-open'
    );
  }

  restoreCartDrawerAutoOpen() {
    if (
      !this
        .pendingCartDrawerComponent
    ) {
      return;
    }

    this.pendingCartDrawerComponent
      .setAttribute(
        'auto-open',
        ''
      );

    this.pendingCartDrawerComponent =
      null;
  }

  async openCartDrawer() {
    const drawer =
      document.querySelector(
        '#cart-drawer'
      );

    if (!drawer) {
      window.location.href =
        this.section.dataset
          .cartPageUrl ||
        '/cart';

      return;
    }

    await customElements.whenDefined(
      'theme-drawer'
    );

    await new Promise(
      (resolve) =>
        requestAnimationFrame(
          () =>
            requestAnimationFrame(
              resolve
            )
        )
    );

    drawer.open?.();
  }

  async addBoxToCart() {
    if (this.isAdding) return;

    const selection =
      this.getSelection();

    const totalQuantity =
      this.getTotalQuantity(
        selection
      );

    const uniqueQuantity =
      this.getUniqueQuantity(
        selection
      );

    if (totalQuantity < 2) return;

    const discountPercent =
      this.getDiscount(
        totalQuantity,
        uniqueQuantity
      );

    const boxId =
      `FOXENA-BOX-${Date.now()}`;

    const items = selection.map(
      (item) => ({
        id: item.variantId,

        quantity:
          item.quantity,

        properties: {
          _foxena_box:
            'Build Your Own Box',

          _foxena_box_id:
            boxId,

          _foxena_box_total:
            String(totalQuantity),

          _foxena_box_unique:
            String(uniqueQuantity),

          _foxena_box_tier:
            `${discountPercent}%`,
        },
      })
    );

    const lines = selection.map(
      (item) => ({
        merchandiseId:
          String(item.variantId),

        quantity:
          item.quantity,
      })
    );

    const sectionIds =
      this.getCartSectionIds();

    const deferredEvent =
      CartLinesUpdateEvent
        .createPromise();

    let addRequestSucceeded =
      false;

    this.isAdding = true;

    this.setStatus(
      'Adding every selected jar ' +
      'to one FOXENA box…'
    );

    this.updateInterface();
    this.pauseCartDrawerAutoOpen();

    this.section.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines,
        promise:
          deferredEvent.promise,
      })
    );

    try {
      const payload = {
        items,
      };

      if (sectionIds.length > 0) {
        payload.sections =
          sectionIds.join(',');

        payload.sections_url =
          window.location.pathname;
      }

      const response = await fetch(
        this.section.dataset
          .cartAddUrl,
        {
          method: 'POST',

          headers: {
            'Content-Type':
              'application/json',

            Accept:
              'application/json',
          },

          body:
            JSON.stringify(payload),
        }
      );

      const responseText =
        await response.text();

      let addResult = {};

      try {
        addResult =
          responseText
            ? JSON.parse(
                responseText
              )
            : {};
      } catch (_) {
        throw new Error(
          'Shopify returned an ' +
          'unreadable cart response.'
        );
      }

      if (
        !response.ok ||
        addResult.status
      ) {
        throw new Error(
          addResult.description ||
          addResult.message ||
          (
            'The complete FOXENA ' +
            'box could not be added.'
          )
        );
      }

      addRequestSucceeded = true;

      const cartResponse =
        await fetch(
          this.section.dataset
            .cartJsonUrl,
          {
            headers: {
              Accept:
                'application/json',
            },
          }
        );

      if (!cartResponse.ok) {
        throw new Error(
          'The box was added, ' +
          'but the cart could not ' +
          'be refreshed.'
        );
      }

      const ajaxCart =
        await cartResponse.json();

      deferredEvent.resolve({
        cart:
          CartLinesUpdateEvent
            .createCartFromAjaxResponse(
              ajaxCart
            ),

        detail: {
          source:
            'foxena-build-box',

          sourceId:
            this.section.id,

          itemCount:
            totalQuantity,

          items:
            ajaxCart.items,

          sections:
            addResult.sections,

          didError:
            false,
        },
      });

      this
        .restoreCartDrawerAutoOpen();

      this.setStatus(
        'Added ✓ Opening your cart…'
      );

      await new Promise(
        (resolve) =>
          window.setTimeout(
            resolve,
            700
          )
      );

      await this.openCartDrawer();

      window.setTimeout(() => {
        this.isAdding = false;

        this.setStatus(
          'Your FOXENA box is ' +
          'in the cart.'
        );

        this.updateInterface();
      }, 900);
    } catch (error) {
      this
        .restoreCartDrawerAutoOpen();

      if (addRequestSucceeded) {
        deferredEvent.reject(error);

        window.location.href =
          this.section.dataset
            .cartPageUrl ||
          '/cart';

        return;
      }

      deferredEvent.reject(error);

      this.section.dispatchEvent(
        new CartErrorEvent({
          error:
            error?.message ||
            'Add to cart failed',

          code: 'INVALID',
        })
      );

      this.isAdding = false;

      this.setStatus(
        error?.message ||
        (
          'Could not add the ' +
          'complete box. ' +
          'Please try again.'
        ),
        true
      );

      this.updateInterface();
    }
  }
}

function initializeFoxenaBoxes(
  root = document
) {
  const sections =
    root.matches?.(
      FOXENA_BOX_SELECTOR
    )
      ? [root]
      : Array.from(
          root.querySelectorAll?.(
            FOXENA_BOX_SELECTOR
          ) || []
        );

  sections.forEach((section) => {
    if (
      initializedSections
        .has(section)
    ) {
      return;
    }

    initializedSections.add(
      section
    );

    new FoxenaBuildBox(
      section
    );
  });
}

initializeFoxenaBoxes();

document.addEventListener(
  'shopify:section:load',
  (event) => {
    initializeFoxenaBoxes(
      event.target
    );
  }
);